import { useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useHookstate } from "@hookstate/core";
import { appStore } from "../../../hookstate/appStore";
import { BrowseTabType } from "../../../types";
import {
  FilterMaps,
  FilterState,
  DEFAULT_FILTER_STATE,
  parseFiltersFromUrl,
  buildUrlPath,
  buildApiParams,
  buildSearchParams,
  getSlugFilterEntities,
} from "../utils/browseUrlParser";

export function useFilterMaps(): FilterMaps | null {
  const initData = useHookstate(appStore.init).get({ noproxy: true });

  return useMemo(() => {
    const {
      comic_types: types,
      comic_genres: genres,
      languages: langs,
    } = initData;
    if (!types || !genres || !langs) return null;

    const filterMaps: FilterMaps = {
      typesBySlug: {},
      typesById: {},
      genresBySlug: {},
      genresById: {},
      langsById: {},
    };
    types.forEach((type) => {
      filterMaps.typesBySlug[type.slug] = type;
      filterMaps.typesById[type.id] = type;
    });
    genres.forEach((genre) => {
      filterMaps.genresBySlug[genre.slug] = genre;
      filterMaps.genresById[genre.id] = genre;
    });
    langs.forEach((lang) => {
      filterMaps.langsById[lang.id] = lang;
    });
    return filterMaps;
  }, [initData]);
}

export function useBrowseFilters(tab: BrowseTabType) {
  const { slug_1, slug_2, slug_3 } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const filterMaps = useFilterMaps();
  const navigate = useNavigate();

  const filters = useMemo((): FilterState | null => {
    if (!filterMaps) return null;
    return parseFiltersFromUrl(slug_1, slug_2, slug_3, searchParams, filterMaps);
  }, [slug_1, slug_2, slug_3, searchParams, filterMaps]);

  const apiParams = useMemo(
    () => (filters ? buildApiParams(filters, tab) : null),
    [filters, tab],
  );

  const setFilters = useCallback(
    (updates: Partial<FilterState>) => {
      if (!filterMaps || !filters) return;
      const merged = { ...filters, ...updates };
      const path = buildUrlPath(merged, filterMaps, tab);
      const params = buildSearchParams(merged, filterMaps, tab);

      // Preserve params that aren't managed by the filter system
      const currentParams = new URLSearchParams(location.search);
      for (const key of ["standalone", "browse_key"]) {
        const val = currentParams.get(key);
        if (val) params.set(key, val);
      }

      params.sort();
      const search = params.toString();
      const fullUrl = search ? `${path}?${search}` : path;
      navigate(fullUrl);
    },
    [filters, filterMaps, tab, navigate, location.search],
  );

  const clearAllFilters = useCallback(() => {
    if (!filterMaps) return;
    // Keep query and sort, reset everything else
    const cleared: FilterState = {
      ...DEFAULT_FILTER_STATE,
      query: filters?.query ?? "",
      sortOrder: filters?.sortOrder ?? DEFAULT_FILTER_STATE.sortOrder,
      sortDuration: filters?.sortDuration ?? DEFAULT_FILTER_STATE.sortDuration,
    };
    const path = buildUrlPath(cleared, filterMaps, tab);
    const params = buildSearchParams(cleared, filterMaps, tab);

    const currentParams = new URLSearchParams(location.search);
    for (const key of ["standalone", "browse_key"]) {
      const val = currentParams.get(key);
      if (val) params.set(key, val);
    }

    params.sort();
    const search = params.toString();
    const fullUrl = search ? `${path}?${search}` : path;
    navigate(fullUrl);
  }, [filters, filterMaps, tab, navigate, location.search]);

  const slugEntities = useMemo(
    () => getSlugFilterEntities(slug_1, slug_2, slug_3, filterMaps),
    [slug_1, slug_2, slug_3, filterMaps],
  );

  return {
    filters,
    apiParams,
    filterMaps,
    setFilters,
    clearAllFilters,
    slugEntities,
    ready: filters !== null,
  };
}
